/**
 * Bane-forhåndsvisning — samme kartvisning som live-kartet, men uten båter:
 * bare bakgrunnskartet og banen (merker + løype + start-/mållinje). Åpnes fra
 * «Baner» i admin via «Vis på kart». Rammer inn banen automatisk.
 */
import { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../lib/api.ts';
import { createCourseLayer, type CourseLayer } from '../lib/courseLayer.ts';

// Grimstad-området som standard utgangspunkt før banen rammes inn.
const DEFAULT_CENTER: [number, number] = [58.34, 8.59];

const KV_ATTR = '© Kartverket';

export function CoursePreviewPage() {
  const { id } = useParams<{ id: string }>();
  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const courseLayerRef = useRef<CourseLayer | null>(null);
  const fitted = useRef(false);

  const course = useQuery({
    queryKey: ['course-geo', id],
    queryFn: () => api.courseGeo(id!),
    enabled: !!id,
    refetchInterval: 15000,
  });

  // Init kart én gang (samme oppsett som live-kartet)
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const el = mapEl.current;
    const map = L.map(el, { zoomControl: true }).setView(DEFAULT_CENTER, 12);

    const graatone = L.tileLayer(
      'https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png',
      { maxZoom: 18, attribution: KV_ATTR },
    );
    const sjokart = L.tileLayer(
      'https://cache.kartverket.no/v1/wmts/1.0.0/sjokartraster/default/webmercator/{z}/{y}/{x}.png',
      { maxZoom: 18, attribution: KV_ATTR },
    );
    const topo = L.tileLayer(
      'https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{z}/{y}/{x}.png',
      { maxZoom: 18, attribution: KV_ATTR },
    );
    const osm = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    });

    graatone.addTo(map);
    L.control.layers(
      { Gråtone: graatone, Sjøkart: sjokart, Topografisk: topo, OpenStreetMap: osm },
      {},
      { collapsed: true },
    ).addTo(map);

    mapRef.current = map;
    courseLayerRef.current = createCourseLayer(map);
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(el);
    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      courseLayerRef.current = null;
      fitted.current = false;
    };
  }, []);

  // Tegn banen + ramme den inn så snart geometrien er lastet
  useEffect(() => {
    courseLayerRef.current?.render(course.data);

    const map = mapRef.current;
    const el = mapEl.current;
    if (!map || !el || el.clientHeight <= 1 || fitted.current) return;
    const pts: [number, number][] = [];
    for (const m of course.data?.marks ?? []) pts.push([m.lat, m.lon]);
    for (const p of course.data?.startLine ?? []) pts.push([p.lat, p.lon]);
    for (const p of course.data?.finishLine ?? []) pts.push([p.lat, p.lon]);
    if (pts.length === 0) return;
    map.invalidateSize();
    map.fitBounds(L.latLngBounds(pts).pad(0.3), { maxZoom: 15 });
    fitted.current = true;
  }, [course.data]);

  const name = course.data?.course?.name ?? 'Bane';
  const markCount = course.data?.marks.length ?? 0;

  return (
    <div
      className="fixed inset-x-0 top-0 z-20 h-screen flex flex-col bg-slate-950"
      style={{ height: '100dvh' }}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 z-[1000]">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/admin/baner" className="text-slate-400 text-sm shrink-0">&larr;</Link>
          <h1 className="font-semibold truncate">🗺️ {name}</h1>
          <span className="text-xs text-slate-500 shrink-0">{markCount} merker</span>
        </div>
      </div>

      <div className="flex-1 relative">
        <div ref={mapEl} className="absolute inset-0" />

        {course.data && markCount === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-slate-900/85 border border-slate-700 rounded-xl px-4 py-3 text-center text-sm text-slate-300">
              Banen har ingen merker med posisjon enda.
              <div className="text-xs text-slate-500 mt-1">
                Legg til merker under «Merker» og knytt dem til banen.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
